import jsPDF from 'jspdf';

interface RefundReceiptData {
    // Dados do cliente
    customerName: string;
    customerEmail: string;
    customerDocument?: string;

    // Dados da transação
    transactionId: string;
    paymentDate: string;
    paymentAmount: number;
    orderAmount: number;
    refundAmount: number;
    refundDate: string;

    // Dados do pagamento
    paymentMethod: string;
    lastDigits?: string;
    cardBrand?: string;
    installments?: number;

    // Dados do produto
    productName?: string;
}

// Formatar valor monetário
const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
};

// Formatar data
const formatDate = (dateStr: string): string => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date);
};

// Formatar documento (CPF/CNPJ)
const formatDocument = (doc: string | null | undefined): string => {
    if (!doc) return '--';
    const numbers = doc.replace(/\D/g, '');

    if (numbers.length === 11) {
        return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (numbers.length === 14) {
        return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
};

// Mascarar últimos dígitos do cartão
const maskCardNumber = (lastDigits: string | undefined): string => {
    if (!lastDigits) return '--';
    return `•••• •••• •••• ${lastDigits}`;
};

// Obter nome da bandeira do cartão
const getCardBrandName = (firstDigits: string | undefined): string => {
    if (!firstDigits) return 'Cartão';

    if (firstDigits.startsWith('4')) return 'Visa';
    if (firstDigits.startsWith('5') && parseInt(firstDigits[1]) >= 1 && parseInt(firstDigits[1]) <= 5) return 'Mastercard';
    if (firstDigits.startsWith('34') || firstDigits.startsWith('37')) return 'American Express';
    if (firstDigits.startsWith('36') || firstDigits.startsWith('38')) return 'Diners Club';
    if (firstDigits.startsWith('6011') || firstDigits.startsWith('65')) return 'Discover';
    if (firstDigits.startsWith('606282')) return 'Hipercard';

    return 'Cartão';
};

// Função para carregar SVG e converter para PNG base64
const loadSvgAsBase64 = (svgPath: string, width: number, height: number): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            const canvas = document.createElement('canvas');
            // Usar resolução maior para melhor qualidade
            const scale = 3;
            canvas.width = width * scale;
            canvas.height = height * scale;

            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.scale(scale, scale);
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/png'));
            } else {
                reject(new Error('Failed to get canvas context'));
            }
        };

        img.onerror = () => reject(new Error('Failed to load SVG'));
        img.src = svgPath;
    });
};

export const generateRefundReceipt = async (data: RefundReceiptData): Promise<void> => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    let y = margin;

    // Cores - Azul Sellyo (#465fff = RGB 70, 95, 255)
    const primaryColor: [number, number, number] = [70, 95, 255]; // Sellyo Blue
    const textDark: [number, number, number] = [17, 24, 39]; // Gray-900
    const textMuted: [number, number, number] = [107, 114, 128]; // Gray-500
    const borderColor: [number, number, number] = [229, 231, 235]; // Gray-200
    const successColor: [number, number, number] = [34, 197, 94]; // Green-500

    // Header com gradiente visual (linha colorida)
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 4, 'F');

    y = 20;

    // Header em duas colunas: Título à esquerda, Logo à direita
    // Título (menor)
    doc.setFontSize(18);
    doc.setTextColor(...textDark);
    doc.setFont('helvetica', 'bold');
    doc.text('Comprovante de Reembolso', margin, y);

    // Logo Sellyo no canto superior direito (carregado do SVG original)
    try {
        const logoBase64 = await loadSvgAsBase64('/images/logo/logo.svg', 120, 33);
        doc.addImage(logoBase64, 'PNG', pageWidth - margin - 40, y - 8, 40, 11);
    } catch (e) {
        // Fallback: texto simples se falhar ao carregar o logo
        doc.setFontSize(14);
        doc.setTextColor(42, 62, 244); // Sellyo blue
        doc.setFont('helvetica', 'bold');
        doc.text('Sellyo', pageWidth - margin - 20, y);
    }

    // Badge de status
    y += 12;
    doc.setFillColor(...primaryColor);
    doc.roundedRect(margin, y - 5, 70, 8, 2, 2, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('REEMBOLSO PROCESSADO', margin + 4, y);

    // Linha divisória elegante
    y += 15;
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);

    // Saudação
    y += 15;
    doc.setFontSize(11);
    doc.setTextColor(...textDark);
    doc.setFont('helvetica', 'normal');
    doc.text(`Olá, ${data.customerName}`, margin, y);

    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(...textMuted);
    const introText = 'Informamos que o reembolso da sua compra foi processado com sucesso. Abaixo estão os detalhes da operação.';
    const introLines = doc.splitTextToSize(introText, contentWidth);
    doc.text(introLines, margin, y);
    y += introLines.length * 5 + 10;

    // Seção: Dados do Reembolso
    doc.setFillColor(249, 250, 251); // Gray-50
    doc.roundedRect(margin, y, contentWidth, 52, 3, 3, 'F');

    y += 10;
    doc.setFontSize(11);
    doc.setTextColor(...primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('DADOS DO REEMBOLSO', margin + 5, y);

    y += 10;
    doc.setFontSize(10);
    doc.setTextColor(...textMuted);
    doc.setFont('helvetica', 'normal');

    // Grid de informações
    const col1X = margin + 5;
    const col2X = pageWidth / 2 + 5;

    // Linha 1
    doc.text('ID da Transação:', col1X, y);
    doc.setTextColor(...textDark);
    doc.setFont('helvetica', 'bold');
    doc.text(data.transactionId.toUpperCase(), col1X + 35, y);

    doc.setTextColor(...textMuted);
    doc.setFont('helvetica', 'normal');
    doc.text('Data do Pagamento:', col2X, y);
    doc.setTextColor(...textDark);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(data.paymentDate), col2X + 42, y);

    // Linha 2
    y += 10;
    doc.setTextColor(...textMuted);
    doc.setFont('helvetica', 'normal');
    doc.text('Valor Reembolsado:', col1X, y);
    doc.setTextColor(...successColor);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(data.refundAmount), col1X + 40, y);

    doc.setTextColor(...textMuted);
    doc.setFont('helvetica', 'normal');
    doc.text('Data do Reembolso:', col2X, y);
    doc.setTextColor(...textDark);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(data.refundDate), col2X + 42, y);


    y += 22;

    // Seção: Dados do Cliente
    doc.setFillColor(249, 250, 251);
    doc.roundedRect(margin, y, contentWidth, 42, 3, 3, 'F');

    y += 10;
    doc.setFontSize(11);
    doc.setTextColor(...primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('DADOS DO CLIENTE', margin + 5, y);

    y += 10;
    doc.setFontSize(10);
    doc.setTextColor(...textMuted);
    doc.setFont('helvetica', 'normal');
    doc.text('Nome:', col1X, y);
    doc.setTextColor(...textDark);
    doc.setFont('helvetica', 'bold');
    doc.text(data.customerName, col1X + 15, y);

    y += 10;
    doc.setTextColor(...textMuted);
    doc.setFont('helvetica', 'normal');
    doc.text('E-mail:', col1X, y);
    doc.setTextColor(...textDark);
    doc.setFont('helvetica', 'bold');
    doc.text(data.customerEmail, col1X + 17, y);

    if (data.customerDocument) {
        doc.setTextColor(...textMuted);
        doc.setFont('helvetica', 'normal');
        doc.text('CPF/CNPJ:', col2X, y);
        doc.setTextColor(...textDark);
        doc.setFont('helvetica', 'bold');
        doc.text(formatDocument(data.customerDocument), col2X + 25, y);
    }

    y += 22;

    // Seção: Forma de Pagamento (se cartão)
    if (data.paymentMethod === 'card' && data.lastDigits) {
        doc.setFillColor(249, 250, 251);
        doc.roundedRect(margin, y, contentWidth, 32, 3, 3, 'F');

        y += 10;
        doc.setFontSize(11);
        doc.setTextColor(...primaryColor);
        doc.setFont('helvetica', 'bold');
        doc.text('FORMA DE PAGAMENTO', margin + 5, y);

        y += 10;
        doc.setFontSize(10);
        doc.setTextColor(...textMuted);
        doc.setFont('helvetica', 'normal');
        doc.text('Cartão:', col1X, y);
        doc.setTextColor(...textDark);
        doc.setFont('helvetica', 'bold');
        doc.text(maskCardNumber(data.lastDigits), col1X + 18, y);

        if (data.cardBrand) {
            doc.setTextColor(...textMuted);
            doc.setFont('helvetica', 'normal');
            doc.text('Bandeira:', col2X, y);
            doc.setTextColor(...textDark);
            doc.setFont('helvetica', 'bold');
            doc.text(getCardBrandName(data.cardBrand), col2X + 22, y);
        }

        y += 22;
    }

    // Aviso importante
    y += 5;
    doc.setFillColor(254, 243, 199); // Yellow-100
    doc.roundedRect(margin, y, contentWidth, 30, 3, 3, 'F');

    y += 8;
    doc.setFontSize(9);
    doc.setTextColor(146, 64, 14); // Yellow-800
    doc.setFont('helvetica', 'bold');
    doc.text('IMPORTANTE', margin + 5, y);

    y += 6;
    doc.setFont('helvetica', 'normal');
    const warningText = 'O prazo para o crédito aparecer em sua conta pode variar de acordo com a operadora do cartão ou instituição financeira. Em caso de pagamento via cartão de crédito, o valor será estornado na próxima fatura ou nas subsequentes.';
    const warningLines = doc.splitTextToSize(warningText, contentWidth - 10);
    doc.text(warningLines, margin + 5, y);

    // Salvar o PDF
    const fileName = `comprovante-reembolso-${data.transactionId}.pdf`;
    doc.save(fileName);
};

export default generateRefundReceipt;
